// Single source of truth for suite-notification severity: the numeric values the
// suite stores in App Configuration, plus how each one is labelled and coloured.
// Shared by the notifications list and the detail view so a severity can never be
// rendered two different ways.
import { PRIORITY_HIGH, PRIORITY_INFO, PRIORITY_NORMAL } from '../Interfaces/Types'

export interface SeverityMeta {
  value: number
  // The C# enum member name. Kept apart from `label` so renaming what the
  // operator reads cannot break parsing of a name off the wire.
  name: string
  label: string
  color: string
  bg: string
}

// Severity colors ride the theme tokens (accessibility themes substitute hues);
// tints mix against the panel surface so they hold up in dark mode.
export const SEVERITIES: SeverityMeta[] = [
  {
    value: PRIORITY_INFO,
    name: 'info',
    label: 'Info',
    color: 'var(--tma-accent)',
    bg: 'color-mix(in srgb, var(--tma-accent) 12%, var(--tma-panel-bg))'
  },
  {
    value: PRIORITY_NORMAL,
    name: 'normal',
    label: 'Normal',
    color: 'var(--tma-warning-text)',
    bg: 'color-mix(in srgb, var(--tma-warning-text) 12%, var(--tma-panel-bg))'
  },
  {
    value: PRIORITY_HIGH,
    name: 'high',
    label: 'High',
    color: 'var(--tma-danger-text)',
    bg: 'color-mix(in srgb, var(--tma-danger-text) 12%, var(--tma-panel-bg))'
  }
]

// What a severity can arrive as. Typed rather than `unknown` so passing a whole
// notification where a severity belongs stays a compile error - that mistake is
// invisible at runtime, since it just renders as Info.
export type RawSeverity = number | string | null | undefined

// The API answers with the numeric severity. Still accept the enum member name and a
// numeric string: a browser on the previous bundle reads "high" from an API pod that
// has not rolled yet, and posts it straight back.
export const normalizeSeverity = (raw: RawSeverity): number | undefined => {
  if (typeof raw === 'number') return SEVERITIES.some((s) => s.value === raw) ? raw : undefined
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (trimmed === '') return undefined
    const numeric = Number(trimmed)
    if (!isNaN(numeric)) return normalizeSeverity(numeric)
    return SEVERITIES.find((s) => s.name === trimmed.toLowerCase())?.value
  }
  return undefined
}

// Falls back to Info for a missing or unrecognised severity so the row still renders.
// Only for display - do not round-trip the fallback back into a save, or an unknown
// severity gets quietly rewritten to Info.
export const sevMeta = (raw: RawSeverity): SeverityMeta => {
  const value = normalizeSeverity(raw)
  return SEVERITIES.find((s) => s.value === value) ?? SEVERITIES[0]
}

// Seeds the edit form. Keeps a severity we cannot render instead of rewriting it to
// Info: the operator may be here to fix a typo, and a silent downgrade on save would
// be indistinguishable from a deliberate change.
export const editableSeverity = (raw: RawSeverity): number => {
  const known = normalizeSeverity(raw)
  if (known !== undefined) return known
  if (typeof raw === 'number') return raw
  const trimmed = (raw ?? '').trim()
  const numeric = Number(trimmed)
  return trimmed !== '' && !isNaN(numeric) ? numeric : PRIORITY_INFO
}

export const isKnownSeverity = (raw: RawSeverity): boolean => normalizeSeverity(raw) !== undefined
